import { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  useScheduledWorkOrders,
  useScheduleStats,
  getPriorityColor,
  getStatusLabel,
  getPriorityLabel,
  ScheduledWorkOrder,
} from '@/hooks/useScheduling';
import {
  ChevronLeft,
  ChevronRight,
  Calendar,
  AlertTriangle,
  CheckCircle,
  Clock,
  Loader2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
const getOrderTypeColor = (orderType: string) => {
  switch (orderType) {
    case 'plotter': return 'bg-cyan-500';
    case 'design_cnc': return 'bg-purple-500';
    case 'mold': return 'bg-orange-500';
    default: return 'bg-blue-500'; // normal
  }
};

import {
  format,
  addDays,
  subDays,
  startOfWeek,
  endOfWeek,
  differenceInDays,
  parseISO,
  isToday,
  isWeekend,
} from 'date-fns';
import { es } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';

interface GanttChartProps {
  daysToShow?: number;
  orderTypeFilter?: string;
}

export function GanttChart({ daysToShow = 21, orderTypeFilter = "all" }: GanttChartProps) {
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState(() =>
    startOfWeek(new Date(), { weekStartsOn: 1 })
  );

  const dateFrom = format(startDate, 'yyyy-MM-dd');
  const dateTo = format(addDays(startDate, daysToShow - 1), 'yyyy-MM-dd');

  const { data: workOrders, isLoading } = useScheduledWorkOrders(dateFrom, dateTo);
  const stats = useScheduleStats(dateFrom, dateTo);

  // Generate days array
  const days = useMemo(() => {
    const result = [];
    for (let i = 0; i < daysToShow; i++) {
      const day = addDays(startDate, i);
      result.push({
        date: format(day, 'yyyy-MM-dd'),
        dayNum: format(day, 'd'),
        dayName: format(day, 'EEE', { locale: es }),
        month: format(day, 'MMM', { locale: es }),
        isToday: isToday(day),
        isWeekend: isWeekend(day),
      });
    }
    return result;
  }, [startDate, daysToShow]);

  const handlePrevious = () => setStartDate(subDays(startDate, 7));
  const handleNext = () => setStartDate(addDays(startDate, 7));
  const handleToday = () => setStartDate(startOfWeek(new Date(), { weekStartsOn: 1 }));

  // Calculate bar position and width
  const getBarStyle = (wo: ScheduledWorkOrder) => {
    const startIdx = Math.max(0, differenceInDays(parseISO(wo.start_date), startDate));
    const endIdx = Math.min(daysToShow - 1, differenceInDays(parseISO(wo.end_date), startDate));
    const width = Math.max(1, endIdx - startIdx + 1);

    return {
      left: `${(startIdx / daysToShow) * 100}%`,
      width: `${(width / daysToShow) * 100}%`,
    };
  };

  // Filter visible work orders
  const visibleWorkOrders = useMemo(() => {
    if (!workOrders) return [];
    return workOrders.filter(wo => {
      const woStart = parseISO(wo.start_date);
      const woEnd = parseISO(wo.end_date);
      const viewStart = startDate;
      const viewEnd = addDays(startDate, daysToShow - 1);
      const matchesDateRange = woStart <= viewEnd && woEnd >= viewStart;
      const matchesType = orderTypeFilter === 'all' || wo.order_type === orderTypeFilter;
      return matchesDateRange && matchesType;
    });
  }, [workOrders, startDate, daysToShow, orderTypeFilter]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <span className="ml-2 text-muted-foreground">Cargando programación...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Stats Summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="p-4">
          <div className="text-sm text-muted-foreground">Total</div>
          <div className="text-2xl font-bold">{stats.totalOrders}</div>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-muted-foreground">Completadas</div>
          <div className="text-2xl font-bold text-green-600">{stats.completedOrders}</div>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-muted-foreground">En Progreso</div>
          <div className="text-2xl font-bold text-blue-600">{stats.inProgressOrders}</div>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-muted-foreground">Pendientes</div>
          <div className="text-2xl font-bold text-gray-600">{stats.pendingOrders}</div>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-muted-foreground">Retrasadas</div>
          <div className="text-2xl font-bold text-red-600">{stats.overdueOrders}</div>
        </Card>
      </div>

      {/* Gantt Chart */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Programación de Producción
              </CardTitle>
              <CardDescription>
                {format(startDate, "d 'de' MMMM", { locale: es })} -{' '}
                {format(addDays(startDate, daysToShow - 1), "d 'de' MMMM yyyy", { locale: es })}
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handlePrevious}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={handleToday}>
                Hoy
              </Button>
              <Button variant="outline" size="sm" onClick={handleNext}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <ScrollArea className="w-full">
            <div className="min-w-[800px]">
              {/* Header with days */}
              <div className="flex border-b mb-2">
                <div className="w-48 shrink-0 p-2 font-medium text-sm">
                  Orden de Trabajo
                </div>
                <div className="flex-1 flex">
                  {days.map((day) => (
                    <div
                      key={day.date}
                      className={cn(
                        'flex-1 text-center p-1 text-xs border-l',
                        day.isToday && 'bg-primary/10',
                        day.isWeekend && 'bg-muted/50'
                      )}
                    >
                      <div className="font-medium">{day.dayName}</div>
                      <div className={cn(
                        'text-muted-foreground',
                        day.isToday && 'text-primary font-bold'
                      )}>
                        {day.dayNum}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Work Orders */}
              {visibleWorkOrders.length > 0 ? (
                <div className="space-y-1">
                  {visibleWorkOrders.map((wo) => (
                    <TooltipProvider key={wo.id}>
                      <div className="flex items-center h-10">
                        {/* Work Order Info */}
                        <div
                          className="w-48 shrink-0 p-2 text-sm truncate cursor-pointer hover:bg-muted/50"
                          onClick={() => navigate(`/work-orders?open=${wo.id}`)}
                        >
                          <span className="font-medium">{wo.order_number}</span>
                          {wo.is_overdue && (
                            <AlertTriangle className="w-3 h-3 text-red-500 inline ml-1" />
                          )}
                        </div>

                        {/* Gantt Bar */}
                        <div className="flex-1 relative h-full">
                          {/* Grid lines */}
                          <div className="absolute inset-0 flex">
                            {days.map((day) => (
                              <div
                                key={day.date}
                                className={cn(
                                  'flex-1 border-l',
                                  day.isToday && 'bg-primary/5',
                                  day.isWeekend && 'bg-muted/30'
                                )}
                              />
                            ))}
                          </div>

                          {/* Bar */}
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div
                                className={cn(
                                  'absolute top-1 bottom-1 rounded cursor-pointer transition-all hover:opacity-80',
                                  wo.order_type ? getOrderTypeColor(wo.order_type) : getPriorityColor(wo.priority),
                                  wo.status === 'completed' && 'opacity-60'
                                )}
                                style={getBarStyle(wo)}
                                onClick={() => navigate(`/work-orders?open=${wo.id}`)}
                              >
                                {/* Progress indicator */}
                                {wo.status === 'in_progress' && (
                                  <div
                                    className="absolute inset-y-0 left-0 bg-black/20 rounded-l"
                                    style={{ width: `${wo.progress}%` }}
                                  />
                                )}
                                {wo.status === 'completed' && (
                                  <CheckCircle className="absolute right-1 top-1/2 -translate-y-1/2 w-4 h-4 text-white" />
                                )}
                                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-white text-xs font-medium truncate pr-6">
                                  {wo.product_name || wo.order_number}
                                </span>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-xs">
                              <div className="space-y-1">
                                <p className="font-bold">{wo.order_number}</p>
                                <p className="text-sm">{wo.product_name}</p>
                                {wo.customer_name && (
                                  <p className="text-sm text-muted-foreground">
                                    Cliente: {wo.customer_name}
                                  </p>
                                )}
                                <div className="flex gap-2 pt-1">
                                  <Badge variant="outline">{getStatusLabel(wo.status)}</Badge>
                                  <Badge variant="outline">{getPriorityLabel(wo.priority)}</Badge>
                                </div>
                                {wo.due_date && (
                                  <p className="text-xs text-muted-foreground">
                                    Entrega: {format(parseISO(wo.due_date), 'dd/MM/yyyy')}
                                  </p>
                                )}
                                {wo.is_overdue && (
                                  <p className="text-xs text-red-500 font-medium">
                                    ¡Retrasada!
                                  </p>
                                )}
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                      </div>
                    </TooltipProvider>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <Calendar className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No hay órdenes de trabajo en este período</p>
                </div>
              )}

              {/* Today indicator line */}
              {days.some(d => d.isToday) && (
                <div
                  className="absolute top-0 bottom-0 w-0.5 bg-red-500 z-10"
                  style={{
                    left: `calc(192px + ${((days.findIndex(d => d.isToday) + 0.5) / daysToShow) * 100}% * (100% - 192px) / 100%)`,
                  }}
                />
              )}
            </div>
            <ScrollBar orientation="horizontal" />
          </ScrollArea>

          {/* Legend */}
          <div className="flex flex-wrap items-center gap-4 mt-4 pt-4 border-t text-sm">
            <span className="text-muted-foreground">Prioridad:</span>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-red-500" />
              <span>Urgente</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-orange-500" />
              <span>Alta</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-blue-500" />
              <span>Normal</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-gray-400" />
              <span>Baja</span>
            </div>
            <span className="ml-4 text-muted-foreground">|</span>
            <div className="flex items-center gap-1">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span>Completada</span>
            </div>
            <div className="flex items-center gap-1">
              <AlertTriangle className="w-4 h-4 text-red-500" />
              <span>Retrasada</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default GanttChart;
